/**
 * HelpFeedbackTab — feedback submission form (Day 60c shell, Day 60d wired).
 *
 * Form UI per COMPONENT_STANDARDS §4: type (Bug / Feature / Other),
 * importance (Low / Medium / High / Critical), title, description,
 * optional screenshot upload, submit.
 *
 * Submit flow (Day 60d):
 *   1. If screenshot present, upload to arbi-feedback bucket first so the
 *      send-feedback Edge Function gets the resulting storage path.
 *   2. Invoke send-feedback Edge Function with the form data + screenshot
 *      path + app_version (VITE_VERCEL_GIT_COMMIT_SHA). The function writes
 *      feedback_items row server-side with submission_method='edge_function'
 *      and fans out admin notifications + email.
 *   3. On Edge Function failure, fall back to direct INSERT via the RLS
 *      authenticated-write policy (submission_method='direct'). Direct
 *      writes carry client-captured metadata (current_route, user_agent)
 *      that the admin inbox treats with appropriate skepticism.
 *   4. Surface success/error toast + reset form on success.
 */
import { useEffect, useRef, useState, type JSX } from 'react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import ArbiSelect from '../ui/ArbiSelect';
import type { ToastState } from '../ui/Toast';

const APP_VERSION: string = (import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ?? 'dev') as string;

type FeedbackType = 'bug' | 'feature' | 'other';
type FeedbackImportance = 'low' | 'medium' | 'high' | 'critical';

const TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other', label: 'Other' },
];

const IMPORTANCE_OPTIONS: { value: FeedbackImportance; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export default function HelpFeedbackTab(): JSX.Element {
  const { user } = useApp();
  const [type, setType] = useState<FeedbackType>('bug');
  const [importance, setImportance] = useState<FeedbackImportance>('medium');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-dismiss toast after 4s — matches COMPONENT_STANDARDS §6 toast spec.
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && !submitting;

  const resetForm = (): void => {
    setType('bug');
    setImportance('medium');
    setTitle('');
    setDescription('');
    setScreenshot(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);

    // §1 — Screenshot upload (optional). Path keyed by user.id so the bucket
    // RLS authenticated-INSERT policy resolves cleanly. UUID prefix on the
    // filename avoids collisions across submissions.
    let screenshotPath: string | null = null;
    if (screenshot && user) {
      const path = `${user.id}/${crypto.randomUUID()}-${screenshot.name}`;
      const { error: upErr } = await supabase.storage
        .from('arbi-feedback')
        .upload(path, screenshot, { contentType: screenshot.type || 'application/octet-stream' });
      if (upErr) {
        setSubmitting(false);
        setToast({ kind: 'error', message: `Screenshot upload failed: ${upErr.message}` });
        return;
      }
      screenshotPath = path;
    }

    // §2 — Edge Function preferred path. Server captures current_route +
    // user_agent from headers; client passes app_version + the optional
    // screenshot path.
    const payload = {
      type,
      importance,
      title: title.trim(),
      description: description.trim(),
      screenshot_path: screenshotPath,
      app_version: APP_VERSION,
    };

    const { data, error } = await supabase.functions.invoke<{
      saved: boolean;
      id?: string;
      error?: string;
    }>('send-feedback', { body: payload });

    if (!error && data?.saved && data.id) {
      setSubmitting(false);
      setToast({ kind: 'success', message: 'Feedback submitted — thanks!' });
      resetForm();
      return;
    }

    // §3 — Direct-write fallback. RLS allows any authenticated user to
    // INSERT (Day 60d Migration 1). submission_method='direct' flags the
    // row so admin inbox can treat client-captured metadata with skepticism.
    const directPayload = {
      submitted_by: user?.id ?? null,
      type,
      importance,
      title: title.trim(),
      description: description.trim(),
      screenshot_path: screenshotPath,
      current_route: window.location.pathname,
      user_agent: navigator.userAgent,
      app_version: APP_VERSION,
      submission_method: 'direct' as const,
    };
    const { error: directErr } = await supabase
      .from('feedback_items')
      .insert(directPayload as never);

    setSubmitting(false);
    if (directErr) {
      setToast({
        kind: 'error',
        message: 'Submission failed — please try again',
      });
      return;
    }
    setToast({ kind: 'success', message: 'Feedback submitted — thanks!' });
    resetForm();
  };

  return (
    <div className="px-4 py-3 space-y-3">
      <p className="text-2xs text-ink-muted leading-relaxed">
        Found a bug or have a feature request? Send it our way.
      </p>

      <div className="space-y-1">
        <label htmlFor="feedback-type" className="text-2xs font-medium text-ink-secondary block">
          Type <span className="text-danger">*</span>
        </label>
        <div id="feedback-type">
          <ArbiSelect
            value={type}
            options={TYPE_OPTIONS}
            onChange={(v) => setType(v as FeedbackType)}
          />
        </div>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="feedback-importance"
          className="text-2xs font-medium text-ink-secondary block"
        >
          Importance <span className="text-danger">*</span>
        </label>
        <div id="feedback-importance">
          <ArbiSelect
            value={importance}
            options={IMPORTANCE_OPTIONS}
            onChange={(v) => setImportance(v as FeedbackImportance)}
          />
        </div>
      </div>

      <div className="space-y-1">
        <label htmlFor="feedback-title" className="text-2xs font-medium text-ink-secondary block">
          Title <span className="text-danger">*</span>
        </label>
        <input
          id="feedback-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Short summary…"
          className="arbi-input px-2 py-1.5 text-xs w-full"
          maxLength={120}
        />
      </div>

      <div className="space-y-1">
        <label
          htmlFor="feedback-description"
          className="text-2xs font-medium text-ink-secondary block"
        >
          Description <span className="text-danger">*</span>
        </label>
        <textarea
          id="feedback-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Steps to reproduce, expected vs actual, why it matters…"
          className="arbi-input px-2 py-1.5 text-xs w-full resize-y min-h-[6rem]"
          rows={5}
        />
      </div>

      <div className="space-y-1">
        <label
          htmlFor="feedback-screenshot"
          className="text-2xs font-medium text-ink-secondary block"
        >
          Screenshot <span className="text-ink-muted">(optional)</span>
        </label>
        <input
          ref={fileInputRef}
          id="feedback-screenshot"
          type="file"
          accept="image/*"
          onChange={(e) => setScreenshot(e.target.files?.[0] ?? null)}
          className="text-2xs text-ink-secondary file:mr-2 file:text-2xs file:px-2 file:py-1 file:rounded file:border file:border-border file:bg-surface-2 file:text-ink-secondary file:cursor-pointer hover:file:bg-surface-3 cursor-pointer w-full"
        />
        {screenshot && <div className="text-2xs text-ink-muted truncate">{screenshot.name}</div>}
      </div>

      <div className="pt-1">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={clsx(
            'arbi-btn-primary text-xs px-3 py-1.5 w-full',
            !canSubmit && 'opacity-50 cursor-not-allowed'
          )}
        >
          {submitting ? 'Submitting…' : 'Submit feedback'}
        </button>
      </div>

      {toast && (
        <div
          role="status"
          className={clsx(
            'text-2xs px-2 py-1.5 rounded border',
            toast.kind === 'success'
              ? 'bg-success/15 border-success/30 text-success'
              : 'bg-danger/15 border-danger/30 text-danger'
          )}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
